import { useEffect, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { dashboardAPI } from '../services/api';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
}

export default function Reports() {
  const [activeTab, setActiveTab] = useState('enrollment');
  const [enrollment, setEnrollment] = useState([]);
  const [claimsAging, setClaimsAging] = useState([]);
  const [premiumSummary, setPremiumSummary] = useState([]);
  const [renewalDue, setRenewalDue] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [ageCompliance, setAgeCompliance] = useState([]);
  const [claimStatus, setClaimStatus] = useState([]);
  const [insurerRecon, setInsurerRecon] = useState([]);
  const [premiumVsClaims, setPremiumVsClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      dashboardAPI.enrollmentReport(),
      dashboardAPI.claimsAging(),
      dashboardAPI.premiumSummary(),
      dashboardAPI.renewalDue(),
      dashboardAPI.endorsements(),
      dashboardAPI.dependentAgeCompliance(),
      dashboardAPI.claimStatus(),
      dashboardAPI.insurerReconciliation(),
      dashboardAPI.premiumVsClaims(),
    ])
      .then(([enrollRes, agingRes, premiumRes, renewalRes, endRes, ageRes, statusRes, reconRes, analyticsRes]) => {
        setEnrollment(enrollRes.data);
        setClaimsAging(agingRes.data);
        setPremiumSummary(premiumRes.data);
        setRenewalDue(renewalRes.data);
        setEndorsements(endRes.data);
        setAgeCompliance(ageRes.data);
        setClaimStatus(statusRes.data);
        setInsurerRecon(reconRes.data);
        setPremiumVsClaims(analyticsRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const tabs = [
    { id: 'enrollment', label: 'Enrollment' },
    { id: 'premium', label: 'Premium Summary' },
    { id: 'claim-status', label: 'Claim Status' },
    { id: 'aging', label: 'Claims Aging' },
    { id: 'analytics', label: 'Premium vs Claims' },
    { id: 'reconciliation', label: 'Insurer Reconciliation' },
    { id: 'renewal', label: 'Policy Renewal' },
    { id: 'endorsements', label: 'Endorsement Log' },
    { id: 'age', label: 'Age Compliance' },
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Reports & Analytics</h1>
        <p className="text-slate-500 mt-1">BRD M9 — enrollment, premium, claims, renewal & compliance reports</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium rounded-lg ${
              activeTab === tab.id ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card overflow-x-auto">
        {activeTab === 'enrollment' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="pb-3 pr-4">Employee</th>
                <th className="pb-3 pr-4 hidden sm:table-cell">Department</th>
                <th className="pb-3 pr-4 hidden md:table-cell">Policy</th>
                <th className="pb-3 pr-4">Type</th>
                <th className="pb-3 pr-4">Enrolled</th>
                <th className="pb-3">Premium</th>
              </tr>
            </thead>
            <tbody>
              {enrollment.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 pr-4">
                    <div className="font-medium">{row.full_name}</div>
                    <div className="text-xs text-slate-400">{row.employee_code}</div>
                  </td>
                  <td className="py-3 pr-4 hidden sm:table-cell">{row.department}</td>
                  <td className="py-3 pr-4 hidden md:table-cell">{row.policy_number}</td>
                  <td className="py-3 pr-4 capitalize">{row.employment_type}</td>
                  <td className="py-3 pr-4">{row.enrollment_date ? new Date(row.enrollment_date).toLocaleDateString('en-IN') : '-'}</td>
                  <td className="py-3">{formatCurrency(row.total_premium)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'premium' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="pb-3 pr-4">Employee</th>
                <th className="pb-3 pr-4">Annual</th>
                <th className="pb-3 pr-4 hidden sm:table-cell">Pro-rata</th>
                <th className="pb-3 pr-4 hidden sm:table-cell">GST (18%)</th>
                <th className="pb-3 pr-4">Total</th>
                <th className="pb-3">Lives</th>
              </tr>
            </thead>
            <tbody>
              {premiumSummary.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-medium">{row.full_name}</td>
                  <td className="py-3 pr-4">{formatCurrency(row.annual_premium)}</td>
                  <td className="py-3 pr-4 hidden sm:table-cell">{formatCurrency(row.pro_rata_premium)}</td>
                  <td className="py-3 pr-4 hidden sm:table-cell">{formatCurrency(row.gst_amount)}</td>
                  <td className="py-3 pr-4 font-medium">{formatCurrency(row.total_premium)}</td>
                  <td className="py-3">{row.covered_lives}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'claim-status' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="pb-3 pr-4">Claim #</th>
                <th className="pb-3 pr-4">Employee</th>
                <th className="pb-3 pr-4">Type</th>
                <th className="pb-3 pr-4">Status</th>
                <th className="pb-3 pr-4">Claimed</th>
                <th className="pb-3 pr-4">Settled</th>
                <th className="pb-3">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {claimStatus.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-medium">{row.claim_number}</td>
                  <td className="py-3 pr-4">{row.employee_name}</td>
                  <td className="py-3 pr-4 capitalize">{row.claim_type?.replace(/_/g, ' ')}</td>
                  <td className="py-3 pr-4 capitalize">{row.status?.replace(/_/g, ' ')}</td>
                  <td className="py-3 pr-4">{formatCurrency(row.claim_amount)}</td>
                  <td className="py-3 pr-4">{formatCurrency(row.settled_amount)}</td>
                  <td className="py-3">{row.submitted_at ? new Date(row.submitted_at).toLocaleDateString('en-IN') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'analytics' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="pb-3 pr-4">Policy</th>
                <th className="pb-3 pr-4">Provider</th>
                <th className="pb-3 pr-4">Premium</th>
                <th className="pb-3 pr-4">Settled Claims</th>
                <th className="pb-3 pr-4">Pending Claims</th>
                <th className="pb-3">Ratio</th>
              </tr>
            </thead>
            <tbody>
              {premiumVsClaims.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-medium">{row.policy_number}</td>
                  <td className="py-3 pr-4">{row.provider_name}</td>
                  <td className="py-3 pr-4">{formatCurrency(row.total_premium)}</td>
                  <td className="py-3 pr-4">{formatCurrency(row.claims_settled)}</td>
                  <td className="py-3 pr-4">{formatCurrency(row.claims_pending)}</td>
                  <td className="py-3">{row.claims_ratio_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'reconciliation' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="pb-3 pr-4">Policy</th>
                <th className="pb-3 pr-4">Insurer</th>
                <th className="pb-3 pr-4">Employees</th>
                <th className="pb-3 pr-4">Premium Due</th>
                <th className="pb-3 pr-4">Collected</th>
                <th className="pb-3 pr-4">Claims Settled</th>
                <th className="pb-3">Pending Endorsements</th>
              </tr>
            </thead>
            <tbody>
              {insurerRecon.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-medium">{row.policy_number}</td>
                  <td className="py-3 pr-4">{row.provider_name}</td>
                  <td className="py-3 pr-4">{row.enrolled_employees}</td>
                  <td className="py-3 pr-4">{formatCurrency(row.premium_due)}</td>
                  <td className="py-3 pr-4">{formatCurrency(row.premium_collected)}</td>
                  <td className="py-3 pr-4">{formatCurrency(row.claims_settled)}</td>
                  <td className="py-3">{row.pending_endorsements}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'aging' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="pb-3 pr-4">Claim #</th>
                <th className="pb-3 pr-4">Employee</th>
                <th className="pb-3 pr-4">Status</th>
                <th className="pb-3 pr-4">Amount</th>
                <th className="pb-3 pr-4">Days Open</th>
                <th className="pb-3">TAT</th>
              </tr>
            </thead>
            <tbody>
              {claimsAging.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-medium">{row.claim_number}</td>
                  <td className="py-3 pr-4">{row.employee_name}</td>
                  <td className="py-3 pr-4 capitalize">{row.status?.replace(/_/g, ' ')}</td>
                  <td className="py-3 pr-4">{formatCurrency(row.claim_amount)}</td>
                  <td className="py-3 pr-4">
                    <span className={row.days_open > (row.claim_settlement_tat || 15) ? 'text-red-600 font-medium' : ''}>
                      {row.days_open}
                    </span>
                  </td>
                  <td className="py-3">{row.claim_settlement_tat || '-'} days</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'renewal' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="pb-3 pr-4">Company</th>
                <th className="pb-3 pr-4">Policy #</th>
                <th className="pb-3 pr-4">Provider</th>
                <th className="pb-3 pr-4">Expires</th>
                <th className="pb-3">Days Left</th>
              </tr>
            </thead>
            <tbody>
              {renewalDue.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 pr-4">{row.company_name}</td>
                  <td className="py-3 pr-4">{row.policy_number}</td>
                  <td className="py-3 pr-4">{row.provider_name}</td>
                  <td className="py-3 pr-4">{new Date(row.policy_end_date).toLocaleDateString('en-IN')}</td>
                  <td className={`py-3 ${row.days_to_expiry <= 30 ? 'text-red-600 font-medium' : ''}`}>{row.days_to_expiry}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'endorsements' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="pb-3 pr-4">Number</th>
                <th className="pb-3 pr-4">Employee</th>
                <th className="pb-3 pr-4">Type</th>
                <th className="pb-3 pr-4">Policy</th>
                <th className="pb-3 pr-4">Status</th>
                <th className="pb-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {endorsements.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-medium">{row.endorsement_number}</td>
                  <td className="py-3 pr-4">{row.employee_name}</td>
                  <td className="py-3 pr-4 capitalize">{row.endorsement_type}</td>
                  <td className="py-3 pr-4">{row.policy_number}</td>
                  <td className="py-3 pr-4 capitalize">{row.status}</td>
                  <td className="py-3">{new Date(row.created_at).toLocaleDateString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeTab === 'age' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-500">
                <th className="pb-3 pr-4">Employee</th>
                <th className="pb-3 pr-4">Dependent</th>
                <th className="pb-3 pr-4">Relation</th>
                <th className="pb-3 pr-4">Age</th>
                <th className="pb-3">Limit</th>
              </tr>
            </thead>
            <tbody>
              {ageCompliance.map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3 pr-4">{row.employee_name}</td>
                  <td className="py-3 pr-4">{row.member_name}</td>
                  <td className="py-3 pr-4 capitalize">{row.relation}</td>
                  <td className="py-3 pr-4 text-amber-600 font-medium">{row.age}</td>
                  <td className="py-3">{row.max_child_age} yrs</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
